import React from 'react';

import { variableVariantToFieldVariant } from '@aline/common';
import { Typography } from '@mui/material';
import { format, parseISO } from 'date-fns';
import type { ExistingFieldRecipient } from 'src/components';
import { AlineAvatar, FieldIcon } from 'src/components';
import { DateFieldIcon } from 'src/components/icons/DateFieldIcon';
import { Apollo } from 'src/gql/generated';
import { getAssigneeDisplayDetails } from 'src/helpers';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  assigneeText: {
    fontWeight: 600,
  },
  avatar: {
    marginRight: theme.spacing(1),
  },
  avatarTextWrapper: {
    display: 'flex',
    flex: '1 1 auto',
    flexDirection: 'column',
    marginLeft: theme.spacing(1),
    // Needed for flex container to shrink below min-content size.
    minWidth: 0,
  },
  avatarWrapper: {
    alignItems: 'center',
    display: 'flex',
    margin: theme.spacing(1, 0, 2),
  },
  completedTimestamp: {
    fontWeight: 600,
    marginLeft: theme.spacing(1),
  },
  contentWrapper: { padding: theme.spacing(2) },
  dateWrapper: {
    alignItems: 'center',
    display: 'flex',
    marginTop: theme.spacing(1),
  },
  emailText: {
    fontSize: 12,
  },
  header: {
    alignItems: 'center',
    backgroundColor: theme.appColors.selectedBackgroundNav,
    borderRadius: '4px 4px 0px 0px',
    display: 'flex',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2),
  },
  icon: {
    height: 18,
    width: 18,
  },
}));

type BaseField =
  | Pick<Apollo.SignatureField, 'id' | '__typename'>
  | Pick<Apollo.DocumentVariable, 'id' | '__typename' | 'variant'>;
type CompletedFieldAction = ExistingFieldRecipient & { completedAt: string | null };
type CompletedField = BaseField & {
  action: CompletedFieldAction | null;
};

interface CompletedFieldPopoverContentProps {
  inputField: CompletedField;
}

export function CompletedFieldPopoverContent({
  inputField,
}: CompletedFieldPopoverContentProps): JSX.Element | null {
  const { classes } = useStyles();
  const completedByText = inputField.__typename === 'SignatureField' ? 'Signed by' : 'Completed by';
  const completedOnText = inputField.__typename === 'SignatureField' ? 'Signed on' : 'Completed on';
  const completedOnTimestamp = inputField.action?.completedAt
    ? `on ${format(parseISO(inputField.action.completedAt), 'M/d/yyyy | hh:mm:ss aaa')}`
    : null;
  const { avatarColor, primary, secondary } = getAssigneeDisplayDetails(
    inputField.action?.assignee
  );

  return (
    <>
      <CompletedFieldPopoverHeader inputField={inputField} />
      <div className={classes.contentWrapper}>
        <Typography noWrap color="textSecondary">
          {completedByText}
        </Typography>
        <div className={classes.avatarWrapper}>
          <AlineAvatar
            avatarInfo={inputField.action?.assignee}
            styles={{ backgroundColor: avatarColor, size: 'md' }}
          />
          <div className={classes.avatarTextWrapper}>
            <Typography noWrap className={classes.assigneeText}>
              {primary}
            </Typography>
            {secondary && (
              <Typography noWrap className={classes.emailText} color="textSecondary">
                {secondary}
              </Typography>
            )}
          </div>
        </div>
        {completedOnTimestamp && (
          <>
            <Typography noWrap color="textSecondary">
              {completedOnText}
            </Typography>
            <div className={classes.dateWrapper}>
              <DateFieldIcon className={classes.icon} />
              <Typography noWrap className={classes.completedTimestamp}>
                {completedOnTimestamp}
              </Typography>
            </div>
          </>
        )}
      </div>
    </>
  );
}

interface CompletedFieldPopoverHeaderProps {
  inputField: BaseField;
}

function CompletedFieldPopoverHeader({
  inputField,
}: CompletedFieldPopoverHeaderProps): JSX.Element {
  const { classes } = useStyles();
  const text = inputField.__typename === 'SignatureField' ? 'Signature details' : 'Field details';
  const fieldVariant =
    inputField.__typename === 'SignatureField'
      ? Apollo.FieldVariant.Signature
      : variableVariantToFieldVariant(inputField.variant);

  return (
    <div className={classes.header}>
      <FieldIcon className={classes.icon} fieldVariant={fieldVariant} />
      <Typography>{text}</Typography>
    </div>
  );
}
